import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { 
  Search, 
  ChevronLeft, 
  ChevronRight,
  Eye,
  Edit,
  Trash2,
  Clock,
  PlayCircle,
  CheckCircle,
  Lock,
  Plus,
  RotateCcw,
  Archive
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";


export default function AdminGames() {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [page, setPage] = useState(0);
  const [editGame, setEditGame] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [bulkPurgeConfirm, setBulkPurgeConfirm] = useState(false);
  const [bulkPurgeTeamId, setBulkPurgeTeamId] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGame, setNewGame] = useState({ team_id: '', opponent_name: '', is_home: true });
  const limit = 20;

  useEffect(() => {
    fetchGames();
    fetchTeams();
  }, [page, statusFilter, archivedFilter]);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/admin/teams?limit=100&status=active');
      setTeams(response.data.teams);
    } catch (error) {
      console.error('Failed to load teams');
    }
  };

  const fetchGames = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', page * limit);
      params.append('limit', limit);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (archivedFilter !== 'all') params.append('archived', archivedFilter);
      if (search) params.append('search', search);
      
      const response = await api.get(`/admin/games?${params}`);
      setGames(response.data.games);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchGames();
  };

  const handleDelete = async (gameId) => {
    try {
      await api.delete(`/admin/games/${gameId}`);
      toast.success('Game archived');
      setDeleteConfirm(null);
      fetchGames();
    } catch (error) {
      toast.error('Failed to archive game');
    }
  };

  const handleHardDelete = async (gameId) => {
    try {
      await api.delete(`/admin/games/${gameId}?hard_delete=true`);
      toast.success('Game permanently deleted');
      setDeleteConfirm(null);
      fetchGames();
    } catch (error) {
      toast.error('Failed to delete game');
    }
  };

  const handleBulkPurge = async () => {
    if (!bulkPurgeTeamId) { toast.error('Select a team first'); return; }
    try {
      const res = await api.delete(`/admin/games/purge-empty?team_id=${bulkPurgeTeamId}`);
      toast.success(res.data.message || 'Empty games purged');
      setBulkPurgeConfirm(false);
      setBulkPurgeTeamId('');
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to purge games');
    }
  };

  const handleRestore = async (gameId) => {
    try {
      await api.post(`/admin/games/${gameId}/restore`);
      toast.success('Game restored');
      fetchGames();
    } catch (error) {
      toast.error('Failed to restore game');
    }
  };

  const handleCreateGame = async () => {
    if (!newGame.team_id || !newGame.opponent_name) {
      toast.error('Team and opponent name are required');
      return;
    }
    try {
      await api.post('/admin/games', newGame);
      toast.success('Game created');
      setCreateDialogOpen(false);
      setNewGame({ team_id: '', opponent_name: '', is_home: true });
      fetchGames();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create game');
    }
  };

  const handleUpdateGame = async () => {
    try {
      await api.patch(`/admin/games/${editGame.game_id}`, {
        opponent_name: editGame.opponent_name,
        status: editGame.status,
        home_score: parseInt(editGame.home_score) || 0,
        away_score: parseInt(editGame.away_score) || 0
      });
      toast.success('Game updated');
      setEditGame(null);
      fetchGames();
    } catch (error) {
      toast.error('Failed to update game');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled':
        return <Clock className="w-4 h-4 text-slate-500" />;
      case 'in_progress':
        return <PlayCircle className="w-4 h-4 text-green-500" />;
      case 'final':
        return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'locked':
        return <Lock className="w-4 h-4 text-purple-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-slate-100 text-slate-700',
      in_progress: 'bg-green-100 text-green-700',
      final: 'bg-blue-100 text-blue-700',
      locked: 'bg-purple-100 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 text-xs font-bold rounded capitalize inline-flex items-center ${styles[status] || 'bg-slate-100'}`}>
        {getStatusIcon(status)}
        <span className="ml-1">{status?.replace('_', ' ')}</span>
      </span>
    );
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">
            Game Management
          </h1>
          <p className="text-slate-600 mt-1">View and manage all games</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setBulkPurgeConfirm(true)} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
            <Trash2 className="w-4 h-4 mr-2" />
            Purge Empty Games
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} className="bg-slate-900">
            <Plus className="w-4 h-4 mr-2" />
            Create Game
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by opponent name..."
              className="h-10"
              data-testid="game-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[160px] h-10">
              <SelectValue placeholder="Game Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="final">Final</SelectItem>
              <SelectItem value="locked">Locked</SelectItem>
            </SelectContent>
          </Select>
          <Select value={archivedFilter} onValueChange={(v) => { setArchivedFilter(v); setPage(0); }}>
            <SelectTrigger className="w-[140px] h-10">
              <SelectValue placeholder="Archive" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Games</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" className="h-10">
            <Search className="w-4 h-4 mr-2" />
            Search
          </Button>
        </form>
      </div>

      {/* Games Table */}
      <div className="bg-white rounded-xl border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Team</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Opponent</th>
                <th className="px-4 py-3 text-center text-xs font-bold uppercase text-slate-600">Score</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">Loading...</td>
                </tr>
              ) : games.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-slate-500">No games found</td>
                </tr>
              ) : (
                games.map((game) => (
                  <tr key={game.game_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {new Date(game.date_time).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          {new Date(game.date_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-semibold text-slate-900">{game.team_name}</p>
                        <p className="text-xs text-slate-500">{game.is_home ? 'Home' : 'Away'}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{game.opponent_name}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-black text-lg text-slate-900">
                        {game.home_score} - {game.away_score}
                      </span>
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(game.status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end space-x-2">
                        <Link to={`/game/${game.game_id}/summary`}>
                          <Button variant="ghost" size="sm" title="View Summary">
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        {!game.archived_at && (
                          <>
                            <Button variant="ghost" size="sm" title="Edit" onClick={() => setEditGame(game)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              title="Archive"
                              onClick={() => setDeleteConfirm({ ...game, action: 'archive' })}
                            >
                              <Archive className="w-4 h-4 text-amber-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              title="Permanently Delete"
                              onClick={() => setDeleteConfirm({ ...game, action: 'hard_delete' })}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </>
                        )}
                        {game.archived_at && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Restore"
                            onClick={() => handleRestore(game.game_id)}
                          >
                            <RotateCcw className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
          <p className="text-sm text-slate-600">
            Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total} games
          </p>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= total}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editGame} onOpenChange={() => setEditGame(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Edit Game</DialogTitle>
          </DialogHeader>
          {editGame && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Opponent Name</label>
                <Input
                  value={editGame.opponent_name || ''}
                  onChange={(e) => setEditGame({...editGame, opponent_name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Home Score</label>
                  <Input
                    type="number"
                    value={editGame.home_score || 0}
                    onChange={(e) => setEditGame({...editGame, home_score: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Away Score</label>
                  <Input
                    type="number"
                    value={editGame.away_score || 0}
                    onChange={(e) => setEditGame({...editGame, away_score: e.target.value})}
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Status</label>
                <Select value={editGame.status} onValueChange={(v) => setEditGame({...editGame, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="final">Final</SelectItem>
                    <SelectItem value="locked">Locked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => setEditGame(null)}>
                  Cancel
                </Button>
                <Button className="flex-1 bg-slate-900" onClick={handleUpdateGame}>
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600">
              {deleteConfirm?.action === 'hard_delete' ? 'Permanently Delete Game' : 'Archive Game'}
            </DialogTitle>
          </DialogHeader>
          {deleteConfirm && (
            <div className="py-4">
              {deleteConfirm.action === 'hard_delete' ? (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm font-bold text-red-800 mb-1">This cannot be undone.</p>
                    <p className="text-sm text-red-700">The game, all box scores, play-by-play events, and edit history will be permanently deleted.</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3 text-sm mb-4">
                    <p><strong>{deleteConfirm.team_name}</strong> vs <strong>{deleteConfirm.opponent_name}</strong></p>
                    <p className="text-slate-500">{new Date(deleteConfirm.date_time).toLocaleDateString()} | Score: {deleteConfirm.home_score}-{deleteConfirm.away_score}</p>
                  </div>
                  <div className="flex space-x-3">
                    <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={() => handleHardDelete(deleteConfirm.game_id)}>
                      <Trash2 className="w-4 h-4 mr-1" /> Permanently Delete
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-slate-600 mb-4">
                    Archive the game vs <strong>{deleteConfirm.opponent_name}</strong>? It can be restored later.
                  </p>
                  <div className="flex space-x-3">
                    <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleDelete(deleteConfirm.game_id)}>
                      <Archive className="w-4 h-4 mr-1" /> Archive Game
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Purge Dialog */}
      <Dialog open={bulkPurgeConfirm} onOpenChange={setBulkPurgeConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase text-red-600">Purge Empty Games</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">This will permanently delete all games with a 0-0 score and no box scores for the selected team. This cannot be undone.</p>
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Team</label>
              <Select value={bulkPurgeTeamId} onValueChange={setBulkPurgeTeamId}>
                <SelectTrigger><SelectValue placeholder="Select team to purge" /></SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.team_id} value={team.team_id}>{team.name} ({team.level})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-3">
              <Button variant="outline" className="flex-1" onClick={() => setBulkPurgeConfirm(false)}>Cancel</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleBulkPurge} disabled={!bulkPurgeTeamId}>
                <Trash2 className="w-4 h-4 mr-1" /> Purge Empty Games
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Game Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-bold uppercase">Create New Game</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Team *</label>
              <Select value={newGame.team_id} onValueChange={(v) => setNewGame({...newGame, team_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  {teams.map((team) => (
                    <SelectItem key={team.team_id} value={team.team_id}>
                      {team.name} ({team.level})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Opponent Name *</label>
              <Input
                value={newGame.opponent_name}
                onChange={(e) => setNewGame({...newGame, opponent_name: e.target.value})}
                placeholder="e.g., Eagles"
              />
            </div>
            <div>
              <label className="text-sm font-bold uppercase text-slate-600 mb-1 block">Home/Away</label>
              <Select value={newGame.is_home ? "home" : "away"} onValueChange={(v) => setNewGame({...newGame, is_home: v === "home"})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex space-x-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button className="flex-1 bg-slate-900" onClick={handleCreateGame}>
                Create Game
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
